import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon, HeartHandshakeIcon, MapPinIcon, SproutIcon, UsersIcon } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { SectionLabel } from '../components/SectionLabel';
import { MONTHLY_HIGHLIGHTS } from '../data/giving';
const GIVE_BACK_HERO = 'https://cdn.magicpatterns.com/patterns/generated-images/c4b71ea8-5a0d-4732-9b3b-fcc9bff4dda4.jpg';
const SUGGEST_EMAIL = 'mailto:jennawing@parnasociety.com?subject=A%20cause%20for%20PAR%E1%B9%86A%20to%20highlight';
const HOW_IT_WORKS = [{
  title: 'One space each month',
  text: 'We choose a single organisation caring for animals, land, water or people, and give it our full attention for the month.',
  Icon: SproutIcon
}, {
  title: 'We practice together',
  text: 'Classes, gatherings and community days all point toward that month\u2019s cause, so showing up on your mat means showing up for it too.',
  Icon: UsersIcon
}, {
  title: 'Everything goes straight there',
  text: 'We never hold the funds. Donations go directly to the organisation, and we share where the month landed when it closes.',
  Icon: HeartHandshakeIcon
}];
export function GiveBack() {
  const [featured, ...previous] = MONTHLY_HIGHLIGHTS;
  return <main className="w-full bg-cream" data-id="element-229" data-mp="3laqt7ykqqmet">
      <section className="relative h-[58svh] min-h-[400px] w-full overflow-hidden bg-shell" data-id="element-230" data-mp="3lbse0urk6n9q">
        <img src={GIVE_BACK_HERO} alt="A soft pastel sunset sky of blush, peach and lavender cloud bands" className="absolute inset-0 h-full w-full object-cover" data-id="element-231" data-mp="cvyz2kcvljus" />
        <div className="absolute inset-x-0 top-0 h-32 bg-cream/40" aria-hidden="true" data-id="element-232" data-mp="354bncnw8e7r" />
        <div className="relative flex h-full flex-col items-center justify-end pb-14 text-center" data-id="element-233" data-mp="83fl3maa0mpg">
          <Reveal data-id="element-234" data-mp="vtjd0we3qy0m">
            <SectionLabel data-id="element-235" data-mp="vt07kf5n223h">Monthly Giving</SectionLabel>
          </Reveal>
          <Reveal delay={0.1} data-id="element-236" data-mp="10m2z6hquv1h1">
            <h1 className="mt-6 max-w-3xl px-6 font-display text-4xl font-light leading-[1.1] text-ink [text-shadow:0_1px_16px_rgba(253,248,244,0.95)] sm:text-6xl" data-id="element-237" data-mp="10n4dqw72y63i">
              Connection doesn&rsquo;t stop
              <br data-id="element-238" data-mp="15g2r3zsogkxd" />
              <span className="italic text-clay" data-id="element-239" data-mp="10ike9ybjhlk3">at the edge of the mat</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="w-full bg-cream py-24 md:py-28" data-id="element-240" data-mp="viy2ltbd0dde">
        <div className="mx-auto max-w-2xl px-6 text-center md:px-10" data-id="element-241" data-mp="rhw2795phv9u">
          <Reveal data-id="element-242" data-mp="rk8ofyihyrsp">
            <p className="text-[15px] font-light leading-[1.9] text-muted" data-id="element-243" data-mp="mrfk3bqxxlh5">
              PARṆA exists to reconnect us to nature, and to our own minds and bodies. But that
              circle isn&rsquo;t complete until it reaches outward, to the people, animals and
              places that need care right now.
            </p>
          </Reveal>
          <Reveal delay={0.1} data-id="element-244" data-mp="7svf7qie3vl4">
            <p className="mt-5 text-[15px] font-light leading-[1.9] text-muted" data-id="element-245" data-mp="7vzoh2apphin">
              So every month we highlight one organisation doing that work somewhere in the world,
              and we give to it together as a community.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="w-full bg-shell py-20 md:py-28" data-id="element-246" data-mp="2jjcxi2s4slgo">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10" data-id="element-247" data-mp="2ohwm6ay40bcq">
          <Reveal data-id="element-248" data-mp="2tfg1zfaggw6s">
            <SectionLabel data-id="element-249" data-mp="2tfy1cksezqix">This month</SectionLabel>
          </Reveal>

          <div className="mx-auto mt-14 grid max-w-[1150px] items-center gap-12 md:grid-cols-2 md:gap-16" data-id="element-250" data-mp="1vs7j7s1ua18h">
            <Reveal data-id="element-251" data-mp="20qr3zp4p56z9">
              <img src={featured.image} alt={featured.imageAlt} className="aspect-[4/3] w-full object-cover" data-id="element-252" data-mp="20s2avxtfuuul" />
            </Reveal>

            <div data-id="element-253" data-mp="2ah3mlbsyaq2r">
              <Reveal delay={0.08} data-id="element-254" data-mp="2ahltd2d8omox">
                <span className="inline-flex items-center gap-2 rounded-full border border-clay/30 px-4 py-2 text-[10px] font-light uppercase tracking-ultra text-clay" data-id="element-255" data-mp="2ain7jfpjxpav">
                  <span className="h-1.5 w-1.5 rounded-full bg-clay/70" aria-hidden="true" data-id="element-256" data-mp="1hyye43kxc1vr" />
                  {featured.month}
                </span>
              </Reveal>

              <Reveal delay={0.14} data-id="element-257" data-mp="3fp4v0ivqburw">
                <h2 className="mt-6 font-display text-4xl font-light leading-[1.15] text-ink sm:text-5xl" data-id="element-258" data-mp="3fpy2jm2iiob3">
                  {featured.name}
                </h2>
              </Reveal>

              <Reveal delay={0.18} data-id="element-259" data-mp="3on8ihkq9z1mc">
                <p className="mt-4 font-display text-xl font-light italic leading-snug text-clay" data-id="element-260" data-mp="78a31yggzusl">
                  {featured.tagline}
                </p>
              </Reveal>

              <Reveal delay={0.22} data-id="element-261" data-mp="2bbahkcgfvwi">
                <p className="mt-5 flex items-center gap-2 text-[11px] font-light uppercase tracking-wider2 text-muted" data-id="element-262" data-mp="g9i06d792pl3">
                  <MapPinIcon className="h-3.5 w-3.5" strokeWidth={1.25} aria-hidden="true" data-id="element-263" data-mp="bayca793j3q3" />
                  {featured.location}
                </p>
              </Reveal>

              <div className="mt-7 max-w-prose space-y-5 text-[15px] font-light leading-[1.9] text-muted" data-id="element-264" data-mp="2d1i8hpsjoc1d">
                {featured.paragraphs.map((paragraph, i) => <Reveal key={paragraph.slice(0, 24)} delay={0.26 + i * 0.05} data-id="element-265" data-mp="slk27vxya65v" data-mpi={i}>
                    <p data-id="element-266" data-mp="xhiw1z21sgdo">{paragraph}</p>
                  </Reveal>)}
              </div>

              <Reveal delay={0.38} data-id="element-267" data-mp="jqnl8h7znp7g">
                <div className="mt-10 flex flex-wrap items-center gap-4" data-id="element-268" data-mp="ns94ckb48zq3">
                  <a href={featured.donateUrl ?? featured.url} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-3 rounded-full border border-clay/40 px-8 py-4 text-[10px] font-light uppercase tracking-ultra text-clay transition-colors duration-500 hover:bg-clay hover:text-cream" data-id="element-269" data-mp="nqekzafhtfo4">
                    Donate to {featured.name}
                    <ArrowUpRightIcon className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-270" data-mp="1v7duyabmez5t" />
                  </a>
                  <a href={featured.url} target="_blank" rel="noopener noreferrer" className="text-[10px] font-light uppercase tracking-ultra text-muted transition-colors duration-300 hover:text-clay" data-id="element-271" data-mp="334qfv8uyv5wd">
                    Learn about their work
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-cream py-24 md:py-32" data-id="element-272" data-mp="3i28x7q5begts">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10" data-id="element-273" data-mp="3i0o84g1rrq3b">
          <Reveal data-id="element-274" data-mp="3d2wd3d5tvnuw">
            <SectionLabel data-id="element-275" data-mp="3d3fmg5hex3xb">How it works</SectionLabel>
          </Reveal>
          <Reveal delay={0.08} data-id="element-276" data-mp="eeqspcj00hln">
            <h2 className="mt-7 text-center font-display text-4xl font-light text-ink sm:text-5xl" data-id="element-277" data-mp="efs6vpvb9k7l">
              Giving, as a practice
            </h2>
          </Reveal>

          <ul className="mx-auto mt-16 grid max-w-[1100px] gap-px overflow-hidden bg-clay/15 md:grid-cols-3" data-id="element-278" data-mp="gzf297g5zyms">
            {HOW_IT_WORKS.map((step, i) => <Reveal as="li" key={step.title} delay={0.06 * i} className="group bg-cream p-9 transition-colors duration-500 hover:bg-shell md:p-11" data-id="element-279" data-mp="qwjgml3rqpt1" data-mpi={i}>
                <step.Icon className="h-5 w-5 text-clay" strokeWidth={1.25} aria-hidden="true" data-id="element-280" data-mp="zvy6bic42wch" />
                <h3 className="mt-6 font-display text-2xl font-light leading-snug text-ink" data-id="element-281" data-mp="14v0yw3awna3k">
                  {step.title}
                </h3>
                <p className="mt-4 text-[14px] font-light leading-[1.85] text-muted" data-id="element-282" data-mp="19ovqdswr4td7">
                  {step.text}
                </p>
              </Reveal>)}
          </ul>
        </div>
      </section>

      <section className="w-full bg-shell py-24 md:py-28" data-id="element-283" data-mp="1nmk1i910om52">
        <div className="mx-auto max-w-3xl px-6 md:px-10" data-id="element-284" data-mp="1nn3inniuf6i1">
          <Reveal data-id="element-285" data-mp="28wzcjhbns4kh">
            <SectionLabel data-id="element-286" data-mp="28wha22yeku44">Where we&rsquo;ve given</SectionLabel>
          </Reveal>

          {previous.length > 0 ? <ul className="mt-12" data-id="element-287" data-mp="23xw55q87hm9c">
              {previous.map((highlight, i) => <Reveal as="li" key={highlight.month} delay={0.05 * i} data-id="element-288" data-mp="2iudj8p7s5ezl" data-mpi={i}>
                  <a href={highlight.url} target="_blank" rel="noopener noreferrer" className="group flex flex-wrap items-baseline gap-x-6 gap-y-1 border-t border-clay/15 py-6 transition-colors duration-500 last:border-b hover:bg-cream" data-id="element-289" data-mp="2dw2ayix4vcjh">
                    <span className="w-32 shrink-0 text-[10px] font-light uppercase tracking-wider2 text-clay" data-id="element-290" data-mp="1zydm6zzy3l95">
                      {highlight.month}
                    </span>
                    <span className="font-display text-xl font-light text-ink" data-id="element-291" data-mp="17fhv8cn8u16d">
                      {highlight.name}
                    </span>
                    <span className="hidden text-[11px] font-light tracking-wide text-muted sm:inline" data-id="element-292" data-mp="12flzf0op9g1w">
                      {highlight.location}
                    </span>
                    <ArrowUpRightIcon className="ml-auto h-4 w-4 shrink-0 text-muted/60 transition-all duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-clay" strokeWidth={1.25} aria-hidden="true" data-id="element-293" data-mp="3ix39k3spdxt3" />
                  </a>
                </Reveal>)}
            </ul> : <Reveal delay={0.08} data-id="element-294" data-mp="5mw0rvf0avbl">
              <p className="mt-10 border-t border-clay/15 pt-10 text-center text-[15px] font-light leading-[1.9] text-muted" data-id="element-295" data-mp="3nv4oqdn0dcza">
                {featured.name} is where we begin. Each month we&rsquo;ll add the next space here,
                so this becomes a record of everywhere this community has reached.
              </p>
            </Reveal>}

          <Reveal delay={0.16} data-id="element-296" data-mp="2gicmpjmrom5l">
            <div className="mt-14 flex flex-col items-center gap-4 text-center" data-id="element-297" data-mp="3d4lk1ht1okym">
              <p className="text-[15px] font-light leading-[1.9] text-muted" data-id="element-298" data-mp="3d6o9o0qacc2t">
                Know a place doing this kind of work? We&rsquo;d love to hear about it.
              </p>
              <div className="flex flex-col items-center gap-4 sm:flex-row" data-id="element-299" data-mp="349xfjtvan7av">
                <a href={SUGGEST_EMAIL} className="group inline-flex items-center gap-3 rounded-full border border-clay/40 px-8 py-4 text-[10px] font-light uppercase tracking-ultra text-clay transition-colors duration-500 hover:bg-clay hover:text-cream" data-id="element-300" data-mp="38c26gkucxptt">
                  Suggest a cause
                  <ArrowUpRightIcon className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-301" data-mp="2per9j6w778hj" />
                </a>
                <Link to="/" state={{
                scrollTo: 'connect'
              }} className="text-[10px] font-light uppercase tracking-ultra text-muted transition-colors duration-300 hover:text-clay" data-id="element-302" data-mp="nl1q6enol8d6">
                  Join the community
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>;
}